import React, { useEffect, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, DragIndicator } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import AppForm from '@/__core/components/AppForm';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { calculateTableWidth } from '@/utils/tableWidth';
import { useSidebarState } from '@/__core/hooks/useSidebarState';
import OrderAutocomplete from '@/modules/commons/components/OrderAutocomplete';
import type { OrderAutocompleteItem } from '@/modules/commons/components/OrderAutocomplete';
import axiosInstance from '@/setup/axios/axiosInstance';
import DeleteConfirmationModal from '@/__core/components/DeleteConfirmationModal';
import FileAttachmentField from '@/__core/components/FileAttachmentField';
import {
  deleteDocument,
  getContentTypeId,
  getDocumentsByObject,
  type Document as AppDocument,
} from '@/modules/documents/redux/DocumentCRUD';

import { AllocationLine, InventoryAllocationFormData } from '../types';

interface AllocationFormProps {
  formData: InventoryAllocationFormData;
  canEdit?: boolean;
  saving?: boolean;
  isDirty?: boolean;
  onChangeForm: (updates: Partial<InventoryAllocationFormData>) => void;
  onInitializeForm?: (updates: Partial<InventoryAllocationFormData>) => void;
  onChangeLine: (lineId: number, updates: Partial<AllocationLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: number) => void;
  onSave: (files: File[]) => Promise<string | boolean | void> | string | boolean | void;
  allocationId?: string;
  onCancel?: () => void;
  orderOptions: OrderAutocompleteItem[];
  loadingOrders: boolean;
  orderSearchInput: string;
  onOrderSearchChange: (value: string) => void;
  storeOptions: Array<{ id: string; name: string; is_default?: boolean }>;
  loadingStores: boolean;
  storeSearchInput: string;
  onStoreSearchChange: (value: string) => void;
  unitOptions: Array<{ id: string; name: string }>;
  loadingUnits: boolean;
  itemOptions: Array<{ id: string; name: string; code?: string; uom_id?: string; uom_name?: string }>;
  loadingItems: boolean;
}

interface SortableAllocationRowProps {
  line: AllocationLine;
  index: number;
  readOnly: boolean;
  itemOptions: Array<{ id: string; name: string; code?: string; uom_id?: string; uom_name?: string }>;
  loadingItems: boolean;
  unitOptions: Array<{ id: string; name: string }>;
  loadingUnits: boolean;
  onChangeLine: (lineId: number, updates: Partial<AllocationLine>) => void;
  onRemoveLine: (lineId: number) => void;
}

const SortableAllocationRow: React.FC<SortableAllocationRowProps> = ({
  line,
  index,
  readOnly,
  itemOptions,
  loadingItems,
  unitOptions,
  loadingUnits,
  onChangeLine,
  onRemoveLine,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: line.id, disabled: readOnly });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };

  return (
    <TableRow ref={setNodeRef} style={style} sx={{ cursor: readOnly ? 'default' : 'grab', '&:hover': { backgroundColor: '#fafafa' } }}>
      <TableCell style={{ width: '44px', minWidth: '44px', paddingLeft: '6px', paddingRight: '2px', position: 'relative' }}>
        {!readOnly && (
          <IconButton size='small' {...attributes} {...listeners}
            sx={{ position: 'absolute', left: 2, top: '5px', zIndex: 1, cursor: 'grab', p: 0, minWidth: 'auto', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
            <DragIndicator fontSize='small' sx={{ color: '#9ca3af' }} />
          </IconButton>
        )}
        <Box sx={{ pl: readOnly ? '0px' : '18px' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textAlign: 'center' }}>{index + 1}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Autocomplete size='small' fullWidth options={itemOptions} loading={loadingItems} filterOptions={(options) => options}
          value={itemOptions.find((i) => i.id === line.item_id) || (line.item_id ? { id: line.item_id, name: line.item_name || '', code: line.item_code || '', uom_id: line.unit_id || '', uom_name: line.unit_name || '' } : null)}
          onChange={(_, newValue) => onChangeLine(line.id, { item_id: newValue?.id || '', item_name: newValue?.name || '', item_code: newValue?.code || '', unit_id: newValue?.uom_id || '', unit_name: newValue?.uom_name || '' })}
          getOptionLabel={(option) => option.name || ''} isOptionEqualToValue={(option, value) => option.id === value.id} disabled={readOnly}
          renderInput={(params) => (<TextField {...params} size='small' placeholder='Nhập để tìm vật tư' sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-root': { backgroundColor: 'transparent', '&:hover': { backgroundColor: '#f9fafb' } } }} />)}
          renderOption={(props, option) => { const { key, ...rest } = props as any; return (<Box component='li' key={key} {...rest}><Typography variant='body2'>{option.code ? `${option.code} - ` : ''}{option.name}</Typography></Box>); }}
          noOptionsText='Không tìm thấy vật tư'
        />
      </TableCell>
      <TableCell>
        <Autocomplete size='small' fullWidth options={unitOptions} loading={loadingUnits} filterOptions={(options) => options}
          value={unitOptions.find((u) => u.id === line.unit_id) || null}
          onChange={(_, newValue) => onChangeLine(line.id, { unit_id: newValue?.id || '', unit_name: newValue?.name || '' })}
          getOptionLabel={(option) => option.name || ''} isOptionEqualToValue={(option, value) => option.id === value.id} disabled={readOnly}
          renderInput={(params) => (<TextField {...params} size='small' placeholder='Chọn ĐVT' sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-root': { backgroundColor: 'transparent', '&:hover': { backgroundColor: '#f9fafb' } } }} />)}
          renderOption={(props, option) => { const { key, ...rest } = props as any; return (<Box component='li' key={key} {...rest}><Typography variant='body2'>{option.name}</Typography></Box>); }}
          noOptionsText='Không tìm thấy ĐVT'
        />
      </TableCell>
      <TableCell align='center'>
        <NumericFormat
          value={line.qty}
          onValueChange={({ value }) => onChangeLine(line.id, { qty: value })}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator='.'
          decimalSeparator=','
          allowNegative={false}
          customInput={TextField}
          size='small'
          disabled={readOnly}
          inputProps={{ style: { textAlign: 'center', width: 90 } }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-root': { backgroundColor: 'transparent', '&:hover': { backgroundColor: '#f9fafb' } } }}
        />
      </TableCell>
      <TableCell align='center'>
        <Tooltip title='Xóa'>
          <IconButton color='error' size='small' onClick={() => onRemoveLine(line.id)} onMouseDown={(e) => e.stopPropagation()} disabled={readOnly}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default function AllocationForm({
  formData,
  canEdit = true,
  saving = false,
  isDirty = false,
  onChangeForm,
  onInitializeForm = onChangeForm,
  onChangeLine,
  onAddLine,
  onRemoveLine,
  onSave,
  allocationId,
  onCancel = () => {},
  orderOptions,
  loadingOrders,
  orderSearchInput,
  onOrderSearchChange,
  storeOptions,
  loadingStores,
  storeSearchInput,
  onStoreSearchChange,
  unitOptions,
  loadingUnits,
  itemOptions,
  loadingItems,
}: AllocationFormProps) {
  const readOnly = !canEdit;
  const sidebarOpen = useSidebarState();
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setTableWidth(calculateTableWidth({ windowWidth: window.innerWidth - 34, sidebarOpen }));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const tableStyles = sharedFormStyles.orderItemsTableStyles;
  const PAPER_HORIZONTAL_PADDING = 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || readOnly) return;

    const lines = formData.lines || [];
    const oldIndex = lines.findIndex((l) => l.id === active.id);
    const newIndex = lines.findIndex((l) => l.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(lines, oldIndex, newIndex);
    onChangeForm({ lines: reordered });
  };

  const displayLines = formData.lines || [];
  const sortableIds = displayLines.map((l) => l.id);

  const [statusOptions, setStatusOptions] = useState<Array<{ id: string; name: string; code?: string; color?: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance.get('/settings/statuses/by-module/', {
          params: { module: 'allocation' },
        });
        if (cancelled) return;
        const data = response.data;
        const allocationData = data?.allocation || data;
        const groups = allocationData?.groups || [];
        const items = groups.flatMap((group: any) => group?.items || []);
        if (items.length > 0) {
          setStatusOptions(
            items.map((item: any) => ({
              id: String(item.id),
              name: item.name || '',
              code: item.code || undefined,
              color: item.color || undefined,
            }))
          );
          const defaultStatus = items.find((item: any) => item.is_default);
          if (defaultStatus) {
            const currentStatus = normalizeStatusValue(formData.status);
            const isCurrentValid = items.some(
              (item: any) =>
                normalizeStatusValue(item.id) === currentStatus ||
                normalizeStatusValue(item.code) === currentStatus
            );
            if (!isCurrentValid) {
              onInitializeForm({
                status: String(defaultStatus.id),
                status_color: defaultStatus.color || '',
              });
            }
          }
        }
      } catch {
        // statusOptions stays empty
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const normalizeStatusValue = (value?: string | null) => (value || '').trim().toUpperCase();

  const selectedStatus = statusOptions.find((status) => {
    const currentStatus = normalizeStatusValue(formData.status);
    return normalizeStatusValue(status.id) === currentStatus || normalizeStatusValue(status.code) === currentStatus;
  }) || null;

  const statusColor = selectedStatus?.color || '';
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = React.useState<AppDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = React.useState(false);
  const [pendingDeleteType, setPendingDeleteType] = React.useState<'file' | 'document' | null>(null);
  const [pendingDeleteIndex, setPendingDeleteIndex] = React.useState<number | null>(null);
  const [pendingDeleteDocId, setPendingDeleteDocId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!allocationId) {
      setUploadedDocuments([]);
      setLoadingDocuments(false);
      return;
    }
    let active = true;
    setLoadingDocuments(true);
    void (async () => {
      try {
        const contentTypeId = await getContentTypeId('allocation');
        const documents = await getDocumentsByObject(contentTypeId, allocationId);
        if (active) setUploadedDocuments(documents);
      } catch {
        if (active) setUploadedDocuments([]);
      } finally {
        if (active) setLoadingDocuments(false);
      }
    })();
    return () => { active = false; };
  }, [allocationId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSelectedFiles((current) => [...current, ...files]);
    event.target.value = '';
  };

  const handleSaveWithFiles = async () => {
    const hadPendingFiles = selectedFiles.length > 0;
    const saved = await onSave(selectedFiles);
    if (saved !== false) {
      setSelectedFiles([]);
      const savedAllocationId = typeof saved === 'string' ? saved : allocationId;
      if (savedAllocationId && hadPendingFiles) {
        setLoadingDocuments(true);
        try {
          const contentTypeId = await getContentTypeId('allocation');
          setUploadedDocuments(await getDocumentsByObject(contentTypeId, savedAllocationId));
        } catch { /* silently fail */ }
        finally { setLoadingDocuments(false); }
      }
    }
  };

  const confirmDeleteAttachment = async () => {
    if (pendingDeleteType === 'file' && pendingDeleteIndex !== null) {
      setSelectedFiles((current) => current.filter((_file, index) => index !== pendingDeleteIndex));
    }
    if (pendingDeleteType === 'document' && pendingDeleteDocId) {
      try {
        await deleteDocument(pendingDeleteDocId);
        setUploadedDocuments((current) => current.filter((doc) => doc.id !== pendingDeleteDocId));
      } catch { /* silently fail */ }
    }
    setPendingDeleteType(null);
    setPendingDeleteIndex(null);
    setPendingDeleteDocId(null);
  };

  const handleDocumentDownload = async (documentId: string, preview = false) => {
    try {
      const response = await axiosInstance.get(`documents/${documentId}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      if (preview) {
        window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }
      const doc = uploadedDocuments.find((item) => item.id === documentId);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc?.name || 'download';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { /* silently fail */ }
  };

  return (
    <AppForm
      onSubmit={handleSaveWithFiles}
      onCancel={onCancel}
      submitting={saving}
      submitDisabled={saving || readOnly || (!isDirty && selectedFiles.length === 0)}
      submitLabel='Lưu'
      submittingLabel='Đang lưu...'
      containerSx={{ m: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Thông tin allocation</Typography>

        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Đơn hàng' labelWidth={100}>
              <OrderAutocomplete
                orders={orderOptions}
                loading={loadingOrders}
                inputValue={orderSearchInput}
                onInputChange={onOrderSearchChange}
                value={formData.order_no || null}
                onChange={(orderId) => onChangeForm({ order_no: orderId || '' })}
                disabled={readOnly}
                placeholder='Nhập để tìm đơn hàng'
              />
            </LabeledField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Kho' labelWidth={100}>
              <Autocomplete
                fullWidth
                options={storeOptions}
                loading={loadingStores}
                value={storeOptions.find((s) => s.id === formData.store_name) || null}
                onChange={(_, newValue) => onChangeForm({ store_name: newValue?.id || '' })}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth placeholder='Chọn kho' />
                )}
                renderOption={(props, option) => {
                  const { key, ...rest } = props as any;
                  return (
                    <Box component='li' key={key} {...rest}>
                      <Typography variant='body2'>{option.name}</Typography>
                    </Box>
                  );
                }}
                noOptionsText='Không tìm thấy kho'
              />
            </LabeledField>
            <LabeledField label='Trạng thái' labelWidth={100}>
              <Autocomplete
                options={statusOptions}
                value={selectedStatus}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} style={{ padding: 0, margin: 0 }}>
                    <Typography sx={{ color: option.color || 'inherit', px: 2, py: 0.5 }}>
                      {option.name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder='Chọn trạng thái'
                    size='small'
                    variant='standard'
                    InputProps={{
                      ...params.InputProps,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: statusColor || formData.status_color || 'inherit',
                        fontWeight: statusColor || formData.status_color ? 500 : 'inherit',
                      },
                    }}
                  />
                )}
                onChange={(_event, newValue) => {
                  const nextStatus = newValue as { id: string; name: string; color?: string } | null;
                  onChangeForm({
                    status: nextStatus?.id || '',
                    status_color: nextStatus?.color || '',
                  });
                }}
                noOptionsText='Không tìm thấy trạng thái'
                disabled={readOnly}
                fullWidth
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <Typography sx={{ ...tableStyles.title, mb: 1 }}>Danh sách vật tư</Typography>

        <TableContainer sx={tableStyles.tableContainer(tableWidth, PAPER_HORIZONTAL_PADDING)}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell style={{ width: '44px', minWidth: '44px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center', pr: 0 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Vật tư</TableCell>
                <TableCell style={{ width: '140px', minWidth: '140px' }} sx={{ fontWeight: 600, color: '#374151' }}>ĐVT</TableCell>
                <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>Số lượng</TableCell>
                <TableCell style={{ width: '50px', minWidth: '50px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {!displayLines || displayLines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align='center' sx={{ py: 4, color: '#94a3b8' }}>
                    Chưa có vật tư nào. Nhấn "Thêm vật tư" để thêm.
                  </TableCell>
                </TableRow>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    {displayLines.map((line, index) => (
                      <SortableAllocationRow
                        key={line.id}
                        line={line}
                        index={index}
                        readOnly={readOnly}
                        itemOptions={itemOptions}
                        loadingItems={loadingItems}
                        unitOptions={unitOptions}
                        loadingUnits={loadingUnits}
                        onChangeLine={onChangeLine}
                        onRemoveLine={onRemoveLine}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {(!readOnly) && (
          <Box sx={{ ...tableStyles.actionBar, mt: 1 }}>
            <Button
              variant='outlined'
              size='small'
              startIcon={<AddIcon />}
              disabled={readOnly}
              onClick={onAddLine}
              sx={{ ...tableStyles.addButton, textTransform: 'none' }}
            >
              Thêm vật tư
            </Button>
          </Box>
        )}
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Ghi chú</Typography>
        <TextareaAutosize
          minRows={4}
          value={formData.note || ''}
          onChange={(event) => onChangeForm({ note: event.target.value })}
          disabled={readOnly}
          placeholder='Nhập ghi chú chung cho allocation...'
          style={sharedFormStyles.additionalTextarea(canEdit)}
        />
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <LabeledField label='File đính kèm' labelWidth={100}>
          <FileAttachmentField
            documents={uploadedDocuments}
            selectedFiles={selectedFiles}
            disabled={readOnly || saving}
            loading={loadingDocuments}
            fileInputKey={`allocation-file-input-${allocationId || 'new'}`}
            onFileSelect={handleFileSelect}
            onRemoveFile={(index) => { setPendingDeleteType('file'); setPendingDeleteIndex(index); }}
            onRemoveDocument={(documentId) => { setPendingDeleteType('document'); setPendingDeleteDocId(documentId); }}
            onPreview={(documentId) => { void handleDocumentDownload(documentId, true); }}
            onDownload={(documentId) => { void handleDocumentDownload(documentId); }}
          />
        </LabeledField>
      </Paper>

      <DeleteConfirmationModal
        open={pendingDeleteType !== null}
        onClose={() => { setPendingDeleteType(null); setPendingDeleteIndex(null); setPendingDeleteDocId(null); }}
        onConfirm={() => { void confirmDeleteAttachment(); }}
        title='Xóa file đính kèm'
        message='Bạn có chắc chắn muốn xóa file này?'
        confirmText='Xóa'
        cancelText='Hủy'
      />
    </AppForm>
  );
}
